/* Unified Inbox · InfiniteGrow AI — design tokens + tiny primitives.
   Scoped only inside the inbox page wrapper so the rest of PremierLab keeps its theme. */

export const ig = {
  ink:        "#10201A",
  inkSoft:    "#1A2F26",
  forest:     "#0B2F23",
  forestDeep: "#071F18",
  emerald:    "#18B86B",
  green:      "#22C55E",
  mint:       "#DDF8EA",
  bg:         "#F7FAF8",
  card:       "#FFFFFF",
  border:     "#E3EAE6",
  borderSoft: "#EEF3F0",
  muted:      "#667085",
  mutedSoft:  "#94A3B0",
  warn:       "#F59E0B",
  error:      "#EF4444",
  info:       "#3B82F6",
  shadow:     "0 1px 2px rgba(16,32,26,0.04), 0 4px 16px rgba(16,32,26,0.04)",
  shadowLg:   "0 8px 32px rgba(16,32,26,0.08), 0 2px 6px rgba(16,32,26,0.04)",
  radius:     12,
  radiusLg:   16,
};

export const channelMeta = {
  sms:       { label: "SMS",        color: "#22C55E", icon: "💬", bg: "#DDF8EA" },
  email:     { label: "Email",      color: "#3B82F6", icon: "✉",  bg: "#E0EBFD" },
  whatsapp:  { label: "WhatsApp",   color: "#16A34A", icon: "🟢", bg: "#DCFCE7" },
  messenger: { label: "Messenger",  color: "#0084FF", icon: "M",  bg: "#DBEAFE" },
  instagram: { label: "Instagram",  color: "#E1306C", icon: "◎",  bg: "#FCE7F3" },
  webchat:   { label: "Web Chat",   color: "#8B5CF6", icon: "◉",  bg: "#EDE9FE" },
  phone:     { label: "Call",       color: "#F59E0B", icon: "☎",  bg: "#FEF3C7" },
  voicemail: { label: "Voicemail",  color: "#F59E0B", icon: "▶",  bg: "#FEF3C7" },
  internal:  { label: "Note",       color: "#94A3B0", icon: "✎",  bg: "#F1F5F4" },
  system:    { label: "System",     color: "#667085", icon: "•",  bg: "#F1F5F4" },
};

export const statusMeta = {
  new:             { label: "New",            tone: "info" },
  open:            { label: "Open",           tone: "info" },
  waiting:         { label: "Waiting",        tone: "neutral" },
  replied:         { label: "Replied",        tone: "success" },
  needs_reply:     { label: "Needs reply",    tone: "warn" },
  needs_follow_up: { label: "Follow-up",      tone: "warn" },
  booked:          { label: "Booked",         tone: "success" },
  in_review:       { label: "In review",      tone: "info" },
  closed:          { label: "Closed",         tone: "neutral" },
  spam:            { label: "Spam",           tone: "error" },
  escalated:       { label: "Escalated",      tone: "error" },
};

const toneColors = {
  success: { fg: "#15803D", bg: "#DCFCE7", border: "#A7F3D0" },
  warn:    { fg: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
  error:   { fg: "#991B1B", bg: "#FEE2E2", border: "#FECACA" },
  info:    { fg: "#1E40AF", bg: "#DBEAFE", border: "#BFDBFE" },
  neutral: { fg: "#475569", bg: "#F1F5F9", border: "#E2E8F0" },
  emerald: { fg: "#065F46", bg: "#D1FAE5", border: "#A7F3D0" },
};

export function Badge({ tone = "neutral", children, ...rest }) {
  const c = toneColors[tone] || toneColors.neutral;
  return (
    <span {...rest} style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 8px", borderRadius: 999,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      fontSize: 10.5, fontWeight: 700, letterSpacing: 0.1, whiteSpace: "nowrap",
      ...(rest.style || {}),
    }}>{children}</span>
  );
}

export function StatusBadge({ status }) {
  const m = statusMeta[status] || { label: status, tone: "neutral" };
  return <Badge tone={m.tone}>{m.label}</Badge>;
}

export function ChannelChip({ channel, size = 14 }) {
  const m = channelMeta[channel] || channelMeta.system;
  return (
    <span title={m.label} style={{
      width: size + 8, height: size + 8, borderRadius: 8, display: "inline-grid", placeItems: "center",
      background: m.bg, color: m.color, fontSize: size - 1, fontWeight: 800, flexShrink: 0,
    }}>{m.icon}</span>
  );
}

export function Avatar({ name, size = 36, photo }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  if (photo) {
    return <img src={photo} alt={name} style={{ width: size, height: size, borderRadius: 999, objectFit: "cover" }} />;
  }
  // Deterministic emerald-family tint per name
  const hash = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const tints = [
    ["#DDF8EA", "#065F46"], ["#D1FAE5", "#047857"], ["#A7F3D0", "#064E3B"],
    ["#E0EBFD", "#1E40AF"], ["#FCE7F3", "#9D174D"], ["#FEF3C7", "#92400E"],
  ];
  const [bg, fg] = tints[hash % tints.length];
  return (
    <span style={{
      width: size, height: size, borderRadius: 999, display: "inline-grid", placeItems: "center",
      background: bg, color: fg, fontWeight: 800, fontSize: size * 0.36, flexShrink: 0,
    }}>{initials}</span>
  );
}

export function IconBtn({ children, onClick, title, active, danger, ...rest }) {
  return (
    <button onClick={onClick} title={title} {...rest} style={{
      width: 32, height: 32, borderRadius: 9, border: `1px solid ${ig.border}`,
      background: active ? ig.mint : ig.card, color: danger ? ig.error : (active ? ig.emerald : ig.ink),
      display: "inline-grid", placeItems: "center", cursor: "pointer", transition: "all 120ms",
      ...(rest.style || {}),
    }}>{children}</button>
  );
}

export function PrimaryBtn({ children, onClick, disabled, ...rest }) {
  return (
    <button onClick={onClick} disabled={disabled} {...rest} style={{
      padding: "9px 14px", borderRadius: 10, border: 0,
      background: disabled ? "#A7F3D0" : `linear-gradient(135deg, ${ig.emerald}, ${ig.green})`,
      color: "#fff", fontWeight: 700, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer",
      boxShadow: disabled ? "none" : "0 6px 16px rgba(24,184,107,0.32)",
      transition: "transform 120ms",
      display: "inline-flex", alignItems: "center", gap: 8,
      ...(rest.style || {}),
    }}>{children}</button>
  );
}

export function GhostBtn({ children, onClick, ...rest }) {
  return (
    <button onClick={onClick} {...rest} style={{
      padding: "8px 12px", borderRadius: 10, border: `1px solid ${ig.border}`,
      background: ig.card, color: ig.ink, fontWeight: 600, fontSize: 12.5, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
      ...(rest.style || {}),
    }}>{children}</button>
  );
}

export function Tag({ children }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 6, background: ig.mint, color: ig.forest,
      fontSize: 10.5, fontWeight: 700, border: `1px solid #BFEED5`, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

export function Skeleton({ width = "100%", height = 12, radius = 6 }) {
  return <div style={{
    width, height, borderRadius: radius,
    background: "linear-gradient(90deg, #EFF4F1 0%, #F8FBF9 50%, #EFF4F1 100%)",
    backgroundSize: "200% 100%", animation: "igSkel 1.4s ease-in-out infinite",
  }} />;
}

export const igGlobalStyles = `
@keyframes igSkel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
@keyframes igFadeIn { from { opacity: 0; transform: translateY(2px); } to { opacity: 1; transform: translateY(0); } }
.ig-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ig-scroll::-webkit-scrollbar-track { background: transparent; }
.ig-scroll::-webkit-scrollbar-thumb { background: ${ig.borderSoft}; border-radius: 999px; border: 2px solid #F7FAF8; }
.ig-scroll::-webkit-scrollbar-thumb:hover { background: #DBE3DE; }
.ig-fade-in { animation: igFadeIn 180ms ease-out; }
`;

export function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}
